/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useSearchParams } from 'react-router';
import { connect } from 'react-redux';
import get from 'lodash/get';
import toLower from 'lodash/toLower';
import sortBy from 'lodash/sortBy';
import groupBy from 'lodash/groupBy';
import filter from 'lodash/filter';

import {
    PageBody,
    HeroImageSectionFront,
    useFlags,
    STATES,
    TERRITORIES,
    ARMED_FORCES,
} from 'sarsaparilla';

import PassComparisonSection from './landing/PassComparisonSection';
import InteragencyAnnualPassSection from './landing/InteragencyAnnualPassSection';
import SiteSpecificDigitalPassesSection from './landing/SiteSpecificDigitalPassesSection';
import SiteSpecificDigitalPassesMapSection from './landing/SiteSpecificDigitalPassesMapSection';

const defaultProps = {
    govtShutdown: false,
};
const propTypes = {
    govtShutdown: PropTypes.bool,
};

function PassLandingPage({ govtShutdown }) {
    const [searchParams] = useSearchParams();
    const sectionId = searchParams.get('section_id');
    const [parkpassFacilitiesListDetails, setParkpassFacilitiesListDetails] = useState(
        []
    );
    const [parkpassFacilitiesList, setParkpassFacilitiesList] = useState([]);
    const [facility, setFacility] = useState('');

    async function getFacilities() {
        try {
            const url = `${process.env.API}/parkpass/facilities`;
            const response = await axios.get(url);
            const data = response.data;

            const facilitiesData = data.facilities.map((facilityData) => ({
                label: facilityData.name,
                value: toLower(facilityData.name.replace(/ /g, '_')),
                id: facilityData.id,
            }));

            const sortedFacilities = sortBy(facilitiesData, ['value']);
            setParkpassFacilitiesListDetails(data.facilities);
            setParkpassFacilitiesList(sortedFacilities);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    }

    const scrollToSection = (id = '') => {
        if (id) {
            const el = document.getElementById(id);
            if (el) {
                const originalTop = Math.floor(el.getBoundingClientRect().top);
                window.scrollBy({ top: originalTop - 100, left: 0, behavior: 'smooth' });
            }
        }
    };

    useEffect(() => {
        getFacilities();
    }, []);

    useEffect(() => {
        if (sectionId) {
            scrollToSection(sectionId);
        }
    }, [sectionId]);

    const states = useMemo(
        () =>
            STATES
                // eslint-disable-next-line react-hooks/exhaustive-deps
                .reduce((acc, item) => ({ ...acc, [item.value]: item.label }), {}),
        [STATES]
    );
    const territories = useMemo(
        () =>
            TERRITORIES
                // eslint-disable-next-line react-hooks/exhaustive-deps
                .reduce((acc, item) => ({ ...acc, [item.value]: item.label }), {}),
        [TERRITORIES]
    );
    const armedForces = useMemo(
        () =>
            ARMED_FORCES
                // eslint-disable-next-line react-hooks/exhaustive-deps
                .reduce((acc, item) => ({ ...acc, [item.value]: item.label }), {}),
        [ARMED_FORCES]
    );
    // eslint-disable-next-line no-shadow
    const isState = (facility) => {
        return !!STATES.find((item) => {
            return facility.state === item.value;
        });
    };
    // eslint-disable-next-line no-shadow
    const isTerritory = (facility) => {
        return !!TERRITORIES.find((item) => {
            return facility.state === item.value;
        });
    };
    // eslint-disable-next-line no-shadow
    const isArmedForce = (facility) => {
        return !!ARMED_FORCES.find((item) => {
            return facility.state === item.value;
        });
    };

    const facilitiesWithState = parkpassFacilitiesListDetails
        .map((item) => {
            let facilityWithState;
            switch (true) {
                case isState(item):
                    facilityWithState = { ...item, state: states[item.state] };
                    break;
                case isTerritory(item):
                    facilityWithState = { ...item, state: territories[item.state] };
                    break;
                case isArmedForce(item):
                    facilityWithState = { ...item, state: armedForces[item.state] };
                    break;
                default:
            }

            return facilityWithState;
        })
        .filter((item) => item)
        .sort((a, b) => a.name.localeCompare(b.name))
        .sort((a, b) => a.state.localeCompare(b.state));
    const handleFilterFacilities = (e) => setFacility(e.target.value);
    const match = (value, key) =>
        get(value, key, '').toLowerCase().indexOf(facility.toLowerCase()) !== -1;
    const filteredFacilities = useMemo(
        () =>
            filter(
                // eslint-disable-next-line react-hooks/exhaustive-deps
                facilitiesWithState,
                (val) =>
                    match(val, 'name') ||
                    match(val, 'state') ||
                    match(val, 'facility_type')
            ),
        [facilitiesWithState]
    );
    const groupedFacilities = useMemo(
        () => Object.entries(groupBy(filteredFacilities, 'state')),
        [filteredFacilities]
    );

    const { interagencyStopExternalSales } = useFlags();

    return (
        <PageBody>
            <div className="pass-landing-page">
                <HeroImageSectionFront
                    title="Buy a Pass"
                    text="Planning a trip to one or more of the thousands of federal recreation sites across the country? Enjoy the convenience of purchasing a pass before your visit to cover entrance or standard amenity (day use) fees. Check out your options below to find the right pass for you!"
                    backgroundImagePath="/img/pass/hero/pass-hero"
                    backgroundImageAltText="Body of water surrounded by hills of trees"
                    backgroundImageColorOverlay="#0C1921"
                    backgroundImageColorOverlayOpacity={0.6}
                />

                <section className="pass-right-for-me-outer-wrapper">
                    <PassComparisonSection
                        govtShutdown={govtShutdown}
                        interagencyStopExternalSales={interagencyStopExternalSales}
                    />
                </section>

                <section className="pass-site-specific-outer-wrapper">
                    <SiteSpecificDigitalPassesSection
                        facilities={parkpassFacilitiesList}
                        govtShutdown={govtShutdown}
                    />
                </section>

                <section className="pass-site-specific-map-outer-wrapper">
                    <SiteSpecificDigitalPassesMapSection
                        facilities={groupedFacilities}
                        facility={facility}
                        onChange={handleFilterFacilities}
                        filteredFacilities={filteredFacilities}
                        govtShutdown={govtShutdown}
                    />
                </section>

                <section className="pass-interagency-annual-outer-wrapper">
                    <InteragencyAnnualPassSection govtShutdown={govtShutdown} />
                </section>
            </div>
        </PageBody>
    );
}

PassLandingPage.propTypes = propTypes;
PassLandingPage.defaultProps = defaultProps;

export default connect((state) => ({
    govtShutdown: get(state, 'auth.banners', []).find(
        ({ governmentShutdown }) => governmentShutdown
    ),
}))(PassLandingPage);
